import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../Profile/Avatar';
import { Username } from '../Profile/Username';
import { BookmarkButton } from '../BookmarkButton';
import { formatTime } from '../../lib/utils/dateUtils';
import type { Event } from '../../types/events';

interface EventCardProps {
  event: Event;
  showStatus?: boolean;
  distance?: number;
  isInactive?: boolean;
}

export function EventCard({ event, showStatus = false, distance, isInactive = false }: EventCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const startDate = new Date(event.start_date);
  const endDate = new Date(event.end_date);
  const currentYear = new Date().getFullYear();
  const startWeekdayShort = startDate.toLocaleDateString(undefined, { weekday: 'short' });
  const isMultiDay = startDate.toDateString() !== endDate.toDateString();
  const isEventOver = endDate < new Date();
  const formatDateCompact = (date: Date) =>
    date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(date.getFullYear() !== currentYear ? { year: 'numeric' } : {})
    });
  const startDateLabel = `${startWeekdayShort}, ${formatDateCompact(startDate)}`;
  const endDateLabel = formatDateCompact(endDate);
  const dateTimeLabel = `${isMultiDay ? `${startDateLabel} - ${endDateLabel}` : startDateLabel} · ${formatTime(startDate)} - ${formatTime(endDate)}`;

  const handleCardClick = () => {
    navigate(`/events/${event.slug}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className={`transition-all duration-150 cursor-pointer active:scale-[0.98] ${isEventOver ? 'opacity-75' : ''}`}
    >
      <div className="aspect-w-16 aspect-h-9 relative rounded-2xl overflow-hidden">
        {event.image_url ? (
          <img
            src={event.image_url}
            alt={event.title}
            className="w-full h-48 object-cover"
            loading="lazy"
          />
        ) : (
          <div
            className="w-full h-48 flex items-center justify-center"
            style={{ backgroundColor: '#F4F7EE' }}
          >
            <span
              className="text-3xl font-semibold text-accent-text font-gelica"
            >
              Event
            </span>
          </div>
        )}
        {isEventOver && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2">
              <span className="text-gray-800 font-semibold text-sm">{t('events.eventOver')}</span>
            </div>
          </div>
        )}
      </div>
      
      <div className="pt-4">
        <div className="flex items-center justify-between mb-4">
          {event.user && (
            <Link
              to={`/profile/${event.user?.username || event.user_id}`}
              className="flex items-center space-x-2 group flex-1 min-w-0"
              onClick={(e) => e.stopPropagation()}
            >
              <Avatar
                url={event.user.avatar_url}
                size="sm"
                userId={event.user_id}
                editable={false}
              />
              <div className="min-w-0">
                <Username
                  username={event.user.username || 'Anonymous'}
                  userId={event.user_id}
                  className="text-sm text-content group-hover:text-accent-text truncate"
                />
                <p className="text-xs text-content/60 mt-0.5">{t('detail.eventOrganiser')}</p>
                {(event.vibe || event.primary_vibe || event.subCategory || event.sub_category_id) && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(event.vibe || event.primary_vibe) && (
                      <span
                        className="inline-flex max-w-full px-2 py-1 text-xs font-medium text-white rounded-full truncate"
                        style={{ backgroundColor: '#C17F59' }}
                      >
                        {event.vibe?.name || event.primary_vibe}
                      </span>
                    )}
                    {(event.subCategory || event.sub_category_id) && (
                      <span className="inline-flex max-w-full px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full truncate">
                        {event.subCategory?.name || event.sub_category_id}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Link>
          )}
          {/* Mobile bookmark - shown on the right side on mobile */}
          <div className="md:hidden flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
            <BookmarkButton targetId={event.id} targetType="events" className="p-1" />
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0">
            {isInactive && (
              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                Inactive
              </span>
            )}
            {showStatus && event.approval_status === 'pending' && (
              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                {t('common.pending')}
              </span>
            )}
          </div>
        </div>

        <h3 className={`text-lg font-semibold mb-2 ${isEventOver ? 'text-content/60' : 'text-content'}`}>
          {event.title}
        </h3>
        
        {isEventOver && (
          <div className="mb-2">
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
              {t('events.eventConcluded')}
            </span>
          </div>
        )}

        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-content/80">
            <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/calendar-icon.png" alt="Date" className="h-4 w-4 mr-2" />
            <span>{dateTimeLabel}</span>
          </div>
          <div className="flex items-center text-sm text-content/80">
            <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/location-icon.png" alt="Location" className="h-4 w-4 mr-2" />
            <span>{event.location}</span>
            {distance !== undefined && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                {distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)}km`}
              </span>
            )}
          </div>
        </div>

        {event.price > 0 && (
          <div className="mt-2">
            <span className="inline-flex items-center px-2 py-1 text-sm font-medium bg-accent-base text-accent-text rounded-full">
              €{event.price}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
